import { useState } from 'react';
import { Camera, RefreshCw, Archive } from 'lucide-react';
import Modal from '../Modal.jsx';
import { useCaptureSnapshot, useBackfillSnapshots } from '../../hooks/useCashFlowSnapshots.js';

/**
 * Modal d'administration du gel des prévisions cash-flow.
 * Accessible via le bouton Camera dans CashFlowPage.
 *
 * Props :
 *   open    boolean   — contrôle l'affichage
 *   onClose () => void
 */
export default function SnapshotAdminModal({ open, onClose }) {
  // Mois sélectionné pour le re-snap manuel (format HTML : "YYYY-MM")
  const today = new Date();
  const currentMonthValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);

  // Toast local minimal
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }

  const captureSnap = useCaptureSnapshot();
  const backfillSnap = useBackfillSnapshots();

  // Action 1 : gel du mois courant
  async function handleSnapCurrentMonth() {
    const period_start = `${currentMonthValue}-01`;
    try {
      const result = await captureSnap.mutateAsync({ period_start });
      showToast('success', `Mois ${period_start} figé : ${result?.captured_count ?? '?'} catégories.`);
    } catch (err) {
      showToast('error', err?.message ?? 'Erreur lors du gel.');
    }
  }

  // Action 2 : re-gel d'un mois choisi
  async function handleSnapSelectedMonth() {
    const [year, month] = selectedMonth.split('-');
    const period_start = `${year}-${month}-01`;
    try {
      const result = await captureSnap.mutateAsync({ period_start });
      showToast('success', `Mois ${period_start} figé : ${result?.captured_count ?? '?'} catégories.`);
    } catch (err) {
      showToast('error', err?.message ?? 'Erreur lors du gel.');
    }
  }

  // Action 3 : backfill 12 mois
  async function handleBackfill() {
    const ok = window.confirm(
      'Backfill des 12 derniers mois — cette opération fige les mois qui ne le sont pas encore, depuis les hypothèses actuelles. Les mois déjà figés sont conservés tels quels. Continuer ?'
    );
    if (!ok) return;
    try {
      const result = await backfillSnap.mutateAsync({ months: 12 });
      showToast(
        'success',
        `Backfill terminé : ${result?.created ?? 0} mois figés, ${result?.skipped ?? 0} déjà figés (ignorés).`
      );
    } catch (err) {
      showToast('error', err?.message ?? 'Erreur lors du backfill.');
    }
  }

  const isBusy = captureSnap.isPending || backfillSnap.isPending;

  return (
    <Modal open={open} onClose={onClose} title="Snapshots cash-flow" size="sm">
      <div className="space-y-5">
        {/* Sous-titre explicatif */}
        <p className="text-xs text-slate-500 leading-relaxed">
          Figer un mois enregistre son prévisionnel : une fois le mois passé, le tableau
          affiche cette valeur figée (cadenas) au lieu de la recalculer. Le gel automatique
          a lieu le 1er de chaque mois, sur le mois qui commence. Ces actions permettent de
          figer ou re-figer un mois à la main, ou de re-générer l'historique.
        </p>

        {/* Action 1 : mois courant */}
        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Camera size={14} className="text-blue-500 shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold text-slate-800">
              Figer le mois courant
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Enregistre le prévisionnel de{' '}
            <span className="font-medium text-slate-700">{formatMonthLabel(currentMonthValue)}</span>{' '}
            tel qu'il est aujourd'hui.
          </p>
          <button
            type="button"
            onClick={handleSnapCurrentMonth}
            disabled={isBusy}
            className="w-full flex items-center justify-center gap-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg px-3 py-2.5 min-h-[44px] transition-colors"
          >
            {captureSnap.isPending ? (
              <>
                <RefreshCw size={13} className="animate-spin" />
                Capture en cours…
              </>
            ) : (
              <>
                <Camera size={13} />
                Figer {formatMonthLabel(currentMonthValue)}
              </>
            )}
          </button>
        </div>

        {/* Action 2 : mois passé au choix */}
        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <RefreshCw size={14} className="text-slate-500 shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold text-slate-800">
              Re-figer un mois passé
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <label htmlFor="snap-month-picker" className="sr-only">
              Choisir le mois
            </label>
            <input
              id="snap-month-picker"
              type="month"
              value={selectedMonth}
              max={currentMonthValue}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="flex-1 text-xs border border-slate-300 rounded-lg px-2 py-2 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-slate-800 bg-white"
            />
            <button
              type="button"
              onClick={handleSnapSelectedMonth}
              disabled={isBusy || !selectedMonth}
              aria-label={`Figer ${selectedMonth}`}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg px-3 py-2.5 min-h-[44px] transition-colors whitespace-nowrap"
            >
              {captureSnap.isPending ? (
                <RefreshCw size={13} className="animate-spin" />
              ) : (
                <Camera size={13} />
              )}
              Figer
            </button>
          </div>
        </div>

        {/* Action 3 : backfill */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Archive size={14} className="text-amber-600 shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold text-slate-800">
              Backfill des 12 derniers mois
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Fige les 12 mois passés depuis les hypothèses d'aujourd'hui — c'est une
            approximation, pas ce qui était prévu à l'époque. Opération lourde ; les mois
            déjà figés sont conservés et ignorés. A utiliser une seule fois après déploiement.
          </p>
          <button
            type="button"
            onClick={handleBackfill}
            disabled={isBusy}
            className="w-full flex items-center justify-center gap-2 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 disabled:opacity-50 rounded-lg px-3 py-2.5 min-h-[44px] border border-amber-300 transition-colors"
          >
            {backfillSnap.isPending ? (
              <>
                <RefreshCw size={13} className="animate-spin" />
                Backfill en cours…
              </>
            ) : (
              <>
                <Archive size={13} />
                Backfill 12 mois
              </>
            )}
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div
            role="status"
            aria-live="polite"
            className={[
              'rounded-lg px-3 py-2.5 text-xs font-medium',
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800',
            ].join(' ')}
          >
            {toast.message}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Helper ────────────────────────────────────────────────────────────────────

function formatMonthLabel(yearMonth) {
  const [year, month] = yearMonth.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}
